import { ERoles } from '@constants';
import { IChatMessageSent, IMessage } from '@sdk/contracts';
import { dateProvider } from '@utils';
import { Image } from 'antd';
import DOMPurify from 'dompurify';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  HtmlWrapper,
  Wrapper,
  TimeWrapper,
  FileWrapper,
  FileInfoWrapper,
  FileIconWrapper,
  ImagePreviewWrapper,
  TextPendingIndicator,
  FilePendingIndicator
} from './ChatMessage.styles';

import { Icon } from '@/components/Icon';
import { PdfViewer } from '@/components/PdfViewer';

type Props = {
  message: IChatMessageSent | IMessage;
  role: ERoles | undefined;
};

const DEFAULT_MESSAGE = '<p><br></p>';

const ChatMessageBase: React.FC<Props> = ({ message, role }) => {
  const { t } = useTranslation();
  const isTeacherOrManager = role === ERoles.TEACHER || role === ERoles.MANAGER;
  const isSentByTeacherOrManager = [ERoles.MANAGER, ERoles.TEACHER].includes(message.sender.role);
  const isCurrentUserSender = message.sender.role === role;

  const isSender = (isTeacherOrManager && isSentByTeacherOrManager) || isCurrentUserSender;
  const isPending = message?.pending === true;

  const removeNbsp = (content: string) => {
    return content.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
  };

  const parseMessageContent = useCallback((content: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content || '', 'text/html');
    const imgs = doc.getElementsByTagName('img');
    const urls: string[] = [];

    for (let i = 0; i < imgs.length; i++) {
      const img = imgs[i];
      urls.push(img.src);
    }

    for (let i = imgs.length - 1; i >= 0; i--) {
      imgs[i].parentNode?.removeChild(imgs[i]);
    }

    const sanitized = DOMPurify.sanitize(doc.body.innerHTML);
    const sanitizedContent = removeNbsp(sanitized);
    return { sanitizedContent, imageUrls: urls };
  }, []);

  const [parsedContent, setParsedContent] = useState<{
    sanitizedContent: string;
    imageUrls: string[];
  }>(() => parseMessageContent(message.content || ''));

  useEffect(() => {
    setParsedContent(parseMessageContent(message.content || ''));
  }, [message.content, parseMessageContent]);

  return (
    <>
      {parsedContent.sanitizedContent && parsedContent.sanitizedContent !== DEFAULT_MESSAGE && (
        <Wrapper key={message._id} $isSender={isSender} $isPending={isPending}>
          <HtmlWrapper dangerouslySetInnerHTML={{ __html: parsedContent.sanitizedContent }} />
          {isPending && <TextPendingIndicator />}
          {parsedContent.imageUrls.length > 0 && (
            <ImagePreviewWrapper>
              {parsedContent.imageUrls.map((url, index) => (
                <Image key={index} src={url} alt={`Image preview ${index + 1}`} />
              ))}
            </ImagePreviewWrapper>
          )}
          <TimeWrapper>{dateProvider.format(message.createdAt, 'HH:mm')}</TimeWrapper>
        </Wrapper>
      )}
      {message.attached && (
        <FileWrapper key={message.attached._id} $pending={isPending}>
          {message.attached.mimetype.includes('pdf') && (
            <PdfViewer height={200} url={message.attached.url} name={message.attached?.name} />
          )}
          <FileInfoWrapper href={message.attached.url} target="_blank">
            <FileIconWrapper>
              <Icon i="Document" size={24} />
            </FileIconWrapper>
            {message.attached.name}
          </FileInfoWrapper>
          {isPending && <FilePendingIndicator>{t('SENDING')}</FilePendingIndicator>}
        </FileWrapper>
      )}
    </>
  );
};

export const ChatMessage = React.memo(ChatMessageBase);
